import { useEffect, useMemo, useState } from 'react';
import { BookOpen, Layers, PencilLine, Plus, Save, Trash2 } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import type { Option } from '@/constants/institute';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import type { AllotmentPayload, TeacherAllocation } from '@/types/allocation';
import type { StaffMember } from '@/types/staff';
import { cn } from '@/lib/utils';
import { allocationsApi } from '../api/allocations.api';
import { subjectsOfSection } from '../sectionSubjects';

/**
 * Arranging what one teacher takes this year.
 *
 * The screen the whole teacher side of the product depends on: until a teacher
 * has sections against their name, their console has nothing to show and their
 * register has nobody on it.
 *
 * The reader edits a list and saves it once, so the endpoint replaces the whole
 * set rather than adding and removing one row at a time — a half-applied edit
 * would put somebody in front of a class nobody expected the next morning.
 *
 * Two rules the server also enforces, mirrored here only so the reader finds
 * out before saving rather than after: a subject in a section has one teacher,
 * and a section has one class teacher.
 */

/** What either way into this screen says once the set is saved. */
export function announceAllotmentSaved(name: string, allocations: TeacherAllocation[]) {
  toast.success(
    allocations.length === 0
      ? `${name} has no classes this year`
      : `${name} takes ${allocations.length === 1 ? '1 class' : `${allocations.length} classes`}`,
    'Their console and register update straight away.',
  );
}

export interface ClassAllotmentModalProps {
  /** Never null — the caller renders this only once somebody is chosen, so the
   *  screen's requests start fresh each time it opens. */
  staff: StaffMember;
  isOpen: boolean;
  onClose: () => void;
  /** Fires with the saved set, so the list behind can update without refetching. */
  onSaved: (allocations: TeacherAllocation[]) => void;
}

/** A row being edited, before it is sent. Keyed by section + subject, as saved. */
interface DraftAllotment {
  classSectionUuid: string;
  subjectUuid: string;
  isClassTeacher: boolean;
}

const rowKey = (row: DraftAllotment) => `${row.classSectionUuid}:${row.subjectUuid}`;

export function ClassAllotmentModal({
  staff,
  isOpen,
  onClose,
  onSaved,
}: ClassAllotmentModalProps) {
  const {
    data: options,
    isLoading: isLoadingOptions,
    error: optionsError,
    refetch: refetchOptions,
  } = useApiQuery(() => allocationsApi.options(), []);

  const {
    data: existing,
    isLoading: isLoadingExisting,
    error: existingError,
    refetch: refetchExisting,
  } = useApiQuery(() => allocationsApi.list(staff.uuid), [staff.uuid]);

  const [draft, setDraft] = useState<DraftAllotment[] | null>(null);
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [isClassTeacher, setIsClassTeacher] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /**
   * The listed row the pickers are changing, by key. Null while adding. An
   * edit replaces that row in place, so it keeps its position and is never
   * checked against itself.
   */
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // The draft starts as what is already arranged, and is only seeded once —
  // re-seeding on every render of the fetched data would discard edits.
  useEffect(() => {
    if (existing && draft === null) {
      // Subjects only. A class-teacher-only allotment is the class screen's to
      // manage, and the server keeps it whatever this list saves.
      setDraft(
        existing.flatMap((allocation) =>
          allocation.subject
            ? [
                {
                  classSectionUuid: allocation.classSectionUuid,
                  subjectUuid: allocation.subject.uuid,
                  isClassTeacher: allocation.isClassTeacher,
                },
              ]
            : [],
        ),
      );
    }
  }, [existing, draft]);

  /** Sections they run the register of without a subject — shown, not edited, here. */
  const registerOnly = useMemo(
    () => (existing ?? []).filter((allocation) => allocation.subject === null),
    [existing],
  );

  /** Section uuid → the class it belongs to, for labels and subject filtering. */
  const sectionIndex = useMemo(() => {
    const index = new Map<
      string,
      { displayName: string; subjects: { uuid: string; name: string }[] }
    >();

    for (const instituteClass of options?.classes ?? []) {
      for (const section of instituteClass.sections) {
        index.set(section.uuid, {
          displayName: section.displayName,
          // The section's own list where it has one, so a Commerce section is
          // never offered Physics.
          subjects: subjectsOfSection(instituteClass, section),
        });
      }
    }
    return index;
  }, [options]);

  const sectionOptions: Option[] = useMemo(
    () =>
      (options?.classes ?? []).flatMap((instituteClass) =>
        instituteClass.sections.map((section) => ({
          value: section.uuid,
          label: section.displayName,
        })),
      ),
    [options],
  );

  /** Only what the chosen section's class actually teaches — the server agrees. */
  const subjectOptions: Option[] = useMemo(() => {
    const section = sectionIndex.get(selectedSection);
    return (section?.subjects ?? []).map((subject) => ({
      value: subject.uuid,
      label: capitalizeWords(subject.name),
    }));
  }, [sectionIndex, selectedSection]);

  const subjectName = (uuid: string) => {
    for (const instituteClass of options?.classes ?? []) {
      const match = instituteClass.subjects.find((subject) => subject.uuid === uuid);
      if (match) return capitalizeWords(match.name);
    }
    return 'Subject';
  };

  /**
   * The pickers as a row, checked against what is already listed.
   *
   * Separate from `addRow` because saving needs the same answer: a section and
   * subject chosen but never added is still what the reader meant to allot.
   */
  function buildRow(allRows: DraftAllotment[]): { row?: DraftAllotment; error?: string } {
    // The row being edited is being replaced, so it cannot clash with itself.
    const rows = editingKey ? allRows.filter((row) => rowKey(row) !== editingKey) : allRows;
    if (!selectedSection || !selectedSubject) {
      return { error: 'Choose a class section and a subject first' };
    }

    const candidate: DraftAllotment = {
      classSectionUuid: selectedSection,
      subjectUuid: selectedSubject,
      isClassTeacher,
    };

    if (rows.some((row) => rowKey(row) === rowKey(candidate))) {
      return { error: 'That class and subject is already on the list' };
    }

    // One section, one class teacher — the server refuses a second, so the
    // reader is told here rather than after a round trip.
    const clash =
      isClassTeacher &&
      rows.find((row) => row.classSectionUuid === selectedSection && row.isClassTeacher);
    if (clash) {
      return {
        error: `They are already class teacher of ${sectionIndex.get(selectedSection)?.displayName ?? 'that section'} through ${subjectName(clash.subjectUuid)}`,
      };
    }

    return { row: candidate };
  }

  function clearPickers() {
    setSelectedSubject('');
    setIsClassTeacher(false);
    if (editingKey) {
      // Leaving an edit empties the section too: it was the edited row's, not
      // a choice the reader made for the next one.
      setSelectedSection('');
      setEditingKey(null);
    }
  }

  /** The list with the pickers' row applied — replacing the edited one, or added at the end. */
  function withRow(rows: DraftAllotment[], row: DraftAllotment): DraftAllotment[] {
    return editingKey
      ? rows.map((existingRow) => (rowKey(existingRow) === editingKey ? row : existingRow))
      : [...rows, row];
  }

  function applyRow() {
    const { row, error } = buildRow(draft ?? []);
    setRowError(error ?? null);
    if (!row) return;

    setDraft(withRow(draft ?? [], row));
    clearPickers();
  }

  /** Loads a listed class into the pickers above, to change its section, subject or class teacher. */
  function startEdit(row: DraftAllotment) {
    setEditingKey(rowKey(row));
    setSelectedSection(row.classSectionUuid);
    setSelectedSubject(row.subjectUuid);
    setIsClassTeacher(row.isClassTeacher);
    setRowError(null);
  }

  function cancelEdit() {
    setRowError(null);
    clearPickers();
  }

  function removeRow(key: string) {
    if (key === editingKey) cancelEdit();
    setDraft((rows) => (rows ?? []).filter((row) => rowKey(row) !== key));
    setRowError(null);
  }

  async function handleSave() {
    if (draft === null) return;

    /**
     * A section and subject sitting in the pickers count as chosen.
     *
     * Without this, choosing them and going straight for Save sent the list as
     * it stood — empty, on the first allotment — and the endpoint replaced the
     * teacher's set with nothing. It answered "has no classes this year", which
     * is true of what was sent and the opposite of what was meant.
     */
    let rows = draft;
    if (selectedSection && selectedSubject) {
      const { row, error } = buildRow(rows);
      if (error) {
        setRowError(error);
        return;
      }
      if (row) {
        rows = withRow(rows, row);
        setDraft(rows);
        clearPickers();
      }
    }

    setIsSaving(true);
    setRowError(null);

    const payload: AllotmentPayload[] = rows.map((row) => ({
      classSectionUuid: row.classSectionUuid,
      subjectUuid: row.subjectUuid,
      isClassTeacher: row.isClassTeacher,
    }));

    try {
      const saved = await allocationsApi.replace(staff.uuid, payload);
      onSaved(saved);
    } catch (caught) {
      // The clashes the server catches — a subject already held by somebody
      // else — name who holds it, so they are worth showing verbatim.
      toast.error(
        'Could not save these classes',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const isLoading = isLoadingOptions || isLoadingExisting;
  const error = optionsError ?? existingError;
  const rows = draft ?? [];
  const hasNothingToAllot = !isLoading && !error && sectionOptions.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Classes for ${staff.fullName}`}
      description={
        options
          ? `${staff.designation ?? 'Teacher'} · ${options.academicYear.name}`
          : staff.designation ?? undefined
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            isLoading={isSaving}
            disabled={isLoading || Boolean(error) || draft === null}
          >
            Save classes
          </Button>
        </>
      }
    >
      {isLoading && <SkeletonLines count={6} />}

      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3">
          <Alert tone="critical" title="Could not load this screen">
            {error.message}
          </Alert>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              refetchOptions();
              refetchExisting();
            }}
          >
            Try again
          </Button>
        </div>
      )}

      {hasNothingToAllot && (
        <EmptyState
          icon={<Layers className="h-5 w-5" />}
          title="No sections to allot yet"
          description="Set up your classes and their sections for this year first — a teacher can only be given a section that exists."
        />
      )}

      {!isLoading && !error && sectionOptions.length > 0 && (
        <div className="flex flex-col gap-6">
          {/* --- add a class ------------------------------------------------ */}
          <div className={cn('card flex flex-col gap-4 p-4', editingKey && 'border-accent')}>
            <h3 className="text-base">{editingKey ? 'Edit this class' : 'Add a class'}</h3>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label="Class and section"
                placeholder="Choose a section"
                options={sectionOptions}
                value={selectedSection}
                onChange={(event) => {
                  setSelectedSection(event.target.value);
                  // The subject list belongs to the class, so a section change
                  // can leave a subject selected that it does not teach.
                  setSelectedSubject('');
                  setRowError(null);
                }}
              />

              <Select
                label="Subject"
                placeholder={selectedSection ? 'Choose a subject' : 'Choose a section first'}
                options={subjectOptions}
                value={selectedSubject}
                disabled={!selectedSection}
                hint={
                  selectedSection && subjectOptions.length === 0
                    ? 'This class has no subjects set up for the year yet'
                    : undefined
                }
                onChange={(event) => {
                  setSelectedSubject(event.target.value);
                  setRowError(null);
                }}
              />
            </div>

            <Checkbox
              label="Also the class teacher of this section"
              checked={isClassTeacher}
              onChange={(event) => {
                setIsClassTeacher(event.target.checked);
                setRowError(null);
              }}
            />

            {rowError && (
              <p role="alert" className="text-xs text-crit">
                {rowError}
              </p>
            )}

            <div className="flex justify-end gap-2">
              {editingKey && (
                <Button variant="ghost" size="sm" onClick={cancelEdit}>
                  Cancel edit
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                leftIcon={
                  editingKey ? <Save className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />
                }
                onClick={applyRow}
              >
                {editingKey ? 'Update class' : 'Add to list'}
              </Button>
            </div>
          </div>

          {/* --- what they take --------------------------------------------- */}
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base">
                Their classes {rows.length > 0 && <span className="text-ink-3">({rows.length})</span>}
              </h3>
              <p className="text-xs text-ink-3">Saved when you choose Save classes</p>
            </div>

            {registerOnly.length > 0 && (
              <p className="text-xs text-ink-2">
                Also class teacher of{' '}
                <span className="font-semibold text-ink">
                  {registerOnly.map((allocation) => allocation.displayName).join(', ')}
                </span>{' '}
                without a subject there. Change that from Classes → ⋮ → Assign class teacher.
              </p>
            )}

            {rows.length === 0 ? (
              <EmptyState
                icon={<BookOpen className="h-5 w-5" />}
                title="No classes yet"
                description="Add a section and subject above. Until then this teacher's console has nothing to show."
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {rows.map((row) => {
                  const key = rowKey(row);
                  const section = sectionIndex.get(row.classSectionUuid);

                  return (
                    <li
                      key={key}
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-field border bg-surface-2 px-3.5 py-3',
                        key === editingKey ? 'border-accent' : 'border-line',
                      )}
                    >
                      <div className="flex min-w-0 flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate text-sm font-[560] text-ink">
                            {section?.displayName ?? 'Section removed'}
                          </span>
                          {row.isClassTeacher && <Badge tone="accent">Class teacher</Badge>}
                        </div>
                        <span className="text-xs text-ink-2">{subjectName(row.subjectUuid)}</span>
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Edit ${section?.displayName ?? 'this section'} ${subjectName(row.subjectUuid)}`}
                          title="Edit"
                          leftIcon={<PencilLine className="h-4 w-4" />}
                          onClick={() => startEdit(row)}
                          disabled={key === editingKey}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove ${section?.displayName ?? 'this section'} ${subjectName(row.subjectUuid)}`}
                          title="Remove"
                          leftIcon={<Trash2 className="h-4 w-4 text-crit" />}
                          onClick={() => removeRow(key)}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
